import React, { useState, useEffect, useRef } from "react";
import * as stripeJs from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe, useElements } from "@stripe/react-stripe-js";

import Purchase from "./types/Purchase";
import ExtraAttrs from "./types/ExtraAttrs";
import Product from "./types/Product";
import Vendor from "./types/Vendor";

import Loading from "./Loading";
import Payment from "./Payment";
import VendorPicker from "./VendorPicker";
import ProductPicker from "./ProductPicker";
import TipPicker from "./TipPicker";
import HoldButton from "./HoldButton";

// const stripePromise = loadStripe('pk_test_QKrWL0MMndVLL9P3AhlIN8Oh') // Stripe Publishable Key
const stripePromise = loadStripe("pk_live_0FW0ag4jzudLThcCw7TQ4b4j"); // Stripe Publishable Key

export default function Form({
  closeModal,
  openModal,
  isModalOpen,
  baseUrl,
  vendorsInitial,
  productsInitial,
  purchase,
  setPurchase,
  modalRef,
}: {
  closeModal: () => void;
  openModal: () => void;
  isModalOpen: boolean;
  baseUrl: string;
  vendorsInitial: Vendor[];
  productsInitial: Product[];
  purchase: Purchase | undefined;
  setPurchase: (purchase: Purchase | undefined) => void;
  modalRef: React.RefObject<HTMLDivElement>;
}) {
  // Form info
  const [vendors, setVendors] = useState<Vendor[]>(
    vendorsInitial.map((vendor: Vendor) => ({ ...vendor, selected: false }))
  );
  const [products, setProducts] = useState<Product[]>(
    productsInitial.map((product: Product) => ({ ...product, quantity: 0 }))
  );
  const [tip, setTip] = useState<number>(0);

  // Stripe info
  // const [email, setEmail] = useState<string>('')
  const [message, setMessage] = useState<string | undefined>();
  const [hasNoVendor, setHasNoVendor] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [paymentIntent, setPaymentIntent] = useState<stripeJs.PaymentIntent | undefined>(undefined);

  const [paymentComplete, setPaymentComplete] = useState<boolean>(false);
  const [done, setDone] = useState<boolean>(false);

  const [formId, setFormId] = useState<string>((Math.random() + 1).toString(36).substring(5));

  const formatPrice = (amount: number) => (amount / 100).toFixed(2);

  const pushAnalytics = (action: string, label: string) => {
    // @ts-ignore
    if (typeof window.dataLayer !== "undefined") {
      // @ts-ignore
      window.dataLayer.push({
        event: "interaction",
        eventCategory: "payments",
        eventAction: action,
        eventLabel: label,
      });
    }
    console.log("pushAnalytics", action, label);
  };

  useEffect(() => {
    if (isModalOpen) {
      pushAnalytics("open", "payments");
    }
  }, [isModalOpen]);

  useEffect(() => {
    const subtotal =
      products.reduce((acc, product) => {
        return acc + (product.quantity || 0) * product.price;
      }, 0) + tip;
    const total = subtotal > 0 ? Math.round(subtotal + 30) / (1 - 0.029) : 0; // https://support.stripe.com/questions/passing-the-stripe-fee-on-to-customers
    if (total > 0 && !paymentComplete) {
      const product_ids: number[] = [];
      products.forEach((p) => {
        if (p.quantity && p.quantity > 0) {
          for (let i = 0; i < p.quantity; i++) {
            product_ids.push(p.id);
          }
        }
      });
      const intent = {
        amount: Math.round(total),
        vendor_id: vendors.find((v) => v.selected)?.id,
        product_ids: product_ids,
        tips: tip,
      };
      fetch(baseUrl + "/purchases/payment_intent", {
        method: paymentIntent ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(paymentIntent ? { ...intent, intent_id: paymentIntent.id } : intent),
      })
        .then((res) => res.json())
        .then((data) => setPaymentIntent(data));
    }
  }, [products, tip]);

  // SUCCESS HANDLING !
  // http://uwbc.localhost/donation-test-page/?payment_intent=pi_3Mip6iFLDDJq2OeF0X4fnaxr&payment_intent_client_secret=pi_3Mip6iFLDDJq2OeF0X4fnaxr_secret_xX0huhRAIzkefZxkmqRY2A6Ui&redirect_status=succeeded
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get("payment_intent_client_secret");

    if (!clientSecret) {
      setIsLoading(false);
      return;
    } else {
      setIsLoading(true);
    }

    const extra: ExtraAttrs = {
      email: params.get("email") || "",
      first_name: params.get("first_name") || "",
      last_name: params.get("last_name") || "",
    };

    stripePromise.then((stripe) => {
      if (stripe) {
        stripe
          .retrievePaymentIntent(clientSecret)
          .then((result) => handlePaymentAttempt(result, undefined, extra, "creditcard"));
      }
    });
  }, []);

  // Check for query params, set fields and open modal if they exist.
  // Otherwise, set fields from cookie.
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const queryVendor = parseInt(query.get("badge") || "", 10);

    if (queryVendor) {
      let hasVendor = false;
      setVendors(
        vendors.map((v, i) => {
          v.selected = v.badge_id === queryVendor;
          if (!hasVendor && v.selected) {
            hasVendor = true;
          }
          return v;
        })
      );
      if (!hasVendor) {
        setHasNoVendor(true);
      }
      openModal();
    }
  }, []);

  // Apple Pay / Payment Request
  const handlePaymentMethod = async (
    e: stripeJs.PaymentRequestPaymentMethodEvent,
    stripe: stripeJs.Stripe,
    extra: ExtraAttrs
  ) => {
    setIsSubmitting(true);
    setMessage(undefined);

    if (paymentIntent && !!paymentIntent.client_secret) {
      const result = await stripe.confirmCardPayment(
        paymentIntent.client_secret,
        {
          payment_method: e.paymentMethod.id,
          // receipt_email: ,
          // address: {}
        },
        { handleActions: false }
      );

      handlePaymentAttempt(result, e, extra, "applepay");
    }
  };

  // Stripe Form submit
  const handlePaymentSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
    stripe: stripeJs.Stripe,
    elements: stripeJs.StripeElements,
    extra: ExtraAttrs
  ) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(undefined);

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Can get redirected elsewhere to handle payment
        // return_url: "https://denimandsteel.ngrok.io",
        return_url: window.location.href,
        payment_method_data: {
          billing_details: {
            email: extra.email,
          },
        },
      },
      redirect: "if_required",
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.

    handlePaymentAttempt(result, undefined, extra, "creditcard");
  };

  const handlePaymentAttempt = async (
    { error, paymentIntent }: stripeJs.PaymentIntentResult,
    e: stripeJs.PaymentRequestPaymentMethodEvent | undefined,
    { email, first_name, last_name }: ExtraAttrs,
    method: "creditcard" | "applepay"
  ) => {
    setIsSubmitting(false);
    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setMessage(error.message);
      } else {
        setMessage(
          "There was a problem processing your payment. Please check your name, address, and payment information and try again. If the problem persists please contact your credit card issuer."
        );
      }
      if (e) {
        e.complete("fail");
      }
    } else if (paymentIntent) {
      switch (paymentIntent.status) {
        case "succeeded":
          setPaymentIntent(paymentIntent);
          setPaymentComplete(true);
          // setMessage("Payment succeeded!")
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage(
            "There was a problem processing your payment. Please check your name, address, and payment information and try again. If the problem persists please contact your credit card issuer."
          );
          break;
      }
    } else {
      setMessage(
        "There was a problem processing your payment. Please check your name, address, and payment information and try again. If the problem persists please contact your credit card issuer."
      );
    }

    if (paymentIntent && paymentIntent.status === "succeeded") {
      const res = await fetch(baseUrl + "/purchases", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          purchase: {
            payment_id: paymentIntent.id,
            payment_method: method,
          },
        }),
      });
      const data = await res.json();
      setPurchase(data);
      setIsLoading(false);
      setIsSubmitting(false);
      pushAnalytics("purchase", "payments");
      if (e) {
        e.complete("success");
      }
    }
  };

  // Reset everything
  const handleComplete = (e: React.MouseEvent) => {
    setTip(0);
    setProducts(productsInitial);
    setVendors(vendorsInitial);
    setMessage(undefined);
    setPaymentIntent(undefined);
    setPurchase(undefined);
    setPaymentComplete(false);
    setDone(false);
  };

  if (isLoading) {
    return <Loading />;
  } else if (paymentComplete || done) {
    const subtotal =
      products.reduce((acc, product) => {
        return acc + (product.quantity || 0) * product.price;
      }, 0) + tip;
    const total = subtotal > 0 ? Math.round(subtotal + 30) / (1 - 0.029) : 0; // https://support.stripe.com/questions/passing-the-stripe-fee-on-to-customers
    const transactionFee = total - subtotal;
    const vendor = vendors.find((v) => v.selected);
    return (
      <>
        {!purchase && (
          <div className="finishing">
            <h2>Finishing Payment</h2>
            <Loading />
          </div>
        )}
        {purchase && (
          <div className="paid">
            <div className="checkmark">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="17 8 10 16 7 12"></polyline>
                <circle cx="12" cy="12" r="10"></circle>
              </svg>
              <div>
                <h4>Payment complete: ${formatPrice(total)}</h4>
                <h2 className="">Show this to {vendor?.name}</h2>
              </div>
            </div>

            <h1 className="font-size-xxl subtotal">${formatPrice(subtotal)}</h1>

            <div className="arrow-down">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="0"
              >
                <rect x="8" y="0" width="8" height="12"></rect>
                <polyline points="2 11 22 11 12 24 2 11"></polyline>
              </svg>
            </div>

            {vendor && (
              <div className="vendor">
                <img src={vendor?.image.cover.url} />
                <div>
                  <h2>{vendor?.name}</h2>
                  <p>Badge {vendor?.badge_id}</p>
                </div>
              </div>
            )}

            <div className="bottom-and-button">
              <HoldButton
                className="button"
                onDone={() => {
                  closeModal();
                  setDone(true);
                }}
              >
                Press and Hold to Close
              </HoldButton>
              {done && (
                <>
                  <button className="button button--outline" onClick={handleComplete}>
                    Start New Transaction
                  </button>
                </>
              )}
              <p className="receipt">
                Receipt sent to <strong>{purchase.email}</strong>
              </p>
            </div>
          </div>
        )}
      </>
    );
  } else {
    const subtotal =
      products.reduce((acc, product) => {
        return acc + (product.quantity || 0) * product.price;
      }, 0) + tip;
    const total = subtotal > 0 ? Math.round(subtotal + 30) / (1 - 0.029) : 0; // https://support.stripe.com/questions/passing-the-stripe-fee-on-to-customers
    const transactionFee = total - subtotal;
    const selectedVendor = vendors.find((v) => v.selected);
    return (
      <div className="payments-form">
        <h1 className="modal-title">Pay Vendor</h1>
        {hasNoVendor && !selectedVendor && (
          <div className="payment-error">
            The vendor you were looking for is not able to receive payments. The vendor should
            contact Megaphone to resolve.
          </div>
        )}
        <VendorPicker vendors={vendors} setVendors={setVendors} modalRef={modalRef} />
        {selectedVendor && (
          <>
            <hr />
            <ProductPicker products={products} setProducts={setProducts} />
            <hr />
            <TipPicker tip={tip} setTip={setTip} vendors={vendors} />
          </>
        )}
        <div className="">
          {total > 0 && (
            <>
              <hr />
              <div className="donate-total">
                <h4 className="price-subtotal">${formatPrice(subtotal)}</h4>
                <h4 className="price-fee">
                  <span>Transaction Fee</span> ${formatPrice(transactionFee)}
                </h4>
                <h4 className="price-total">
                  <strong>${formatPrice(total)}</strong>
                </h4>
              </div>
            </>
          )}

          {total > 0 && (
            <div className="payment">
              {paymentIntent && paymentIntent.client_secret ? (
                <Elements
                  key={paymentIntent.id}
                  stripe={stripePromise}
                  options={{
                    clientSecret: paymentIntent.client_secret,
                    fonts: [
                      // {
                      //   family: "Lato",
                      //   src: "url(https://fonts.gstatic.com/s/lato/v24/S6uyw4BMUTPHjx4wXiWtFCc.woff2) format('woff2')",
                      //   // weight: "bold",
                      // },
                      // {
                      //   family: "Lato",
                      //   src: "url(http://dev-megaphone-wordpress.pantheonsite.io/wp-content/themes/megaphone/src/assets/fonts/Lato-Bold.ttf) format('ttf')",
                      //   weight: "900",
                      // },
                      // {
                      //   family: "Lato",
                      //   src: "url(https://fonts.googleapis.com/css2?family=Lato:wght@400&display=swap)",
                      //   weight: "400",
                      // },
                      // {
                      //   family: "Lato",
                      //   src: "url(https://fonts.googleapis.com/css2?family=Lato:wght@700&display=swap)",
                      //   weight: "700",
                      // },
                      // {
                      //   family: 'Avenir',
                      //   src: 'url(https://uwbc.ca/wp-content/themes/united-way-bc/src/assets/fonts/AvenirLTStd-Roman.otf)',
                      //   weight: '400',
                      // },
                      // {
                      //   family: 'Avenir',
                      //   src: 'url(https://uwbc.ca/wp-content/themes/united-way-bc/src/assets/fonts/AvenirLTStd-Heavy.otf)',
                      //   weight: '600',
                      // },
                      // {
                      //   family: 'Avenir',
                      //   src: 'url(https://uwbc.ca/wp-content/themes/united-way-bc/src/assets/fonts/AvenirLTStd-Black.otf)',
                      //   weight: '700',
                      // },
                    ],
                    appearance: {
                      theme: "stripe",
                      variables: {
                        // spacingUnit: '5px',
                        // @ts-ignore
                        gridRowSpacing: "18px",
                        // colorPrimary: '#ff0000',
                        fontWeightNormal: "bold",
                        fontFamily: "Lato, sans-serif",
                      },
                    },
                  }}
                >
                  <Payment
                    amount={total}
                    onPaymentMethod={handlePaymentMethod}
                    onPaymentSubmit={handlePaymentSubmit}
                    submitting={isSubmitting}
                    validated={true}
                    message={message}
                  />
                </Elements>
              ) : (
                <div className="mt-4">
                  <Loading />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}
